import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import PropTypes from 'prop-types';
import { Button, Dialog } from '@mui/material';
import MealDetailEditableMobile from '../meal/meal_detail_editable_mobile';
import {
  mealWithArraySectionDeleted,
  mealWithArraySectionUpdated,
  mealWithIngredientTokensReset,
  mealWithIngredientTokensUpdated,
  mealWithNewArraySectionItem,
  mealWithStructuredIngredientUpdated,
} from '../../services/meals';
import {
  analyzeMissingIngredientsOperation,
  deriveNutritionFromIngredientsOperation,
  getSuggestionsFromHistoryOperation,
  getSuggestionsOperation,
} from '../../operations/meal_operations';
import { userLocale } from '../../services/users';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';

const showNutritionMetrics = [
  'calories',
  'fat',
  'carbohydrate',
  'protein',
  'fibre',
];

const LogFoodItemMealDetailPage = ({ meal, onClickLog, onCloseMealDetail }) => {
  const [derivedNutrition, setDerivedNutrition] = useState(null);
  const [scaleToServings, setScaleToServings] = useState(1);
  const [currentlyEditingInline, setCurrentlyEditingInline] = useState({
    section: null,
    itemId: null,
  });
  const currentTextRef = useRef();
  const currentRecipeRef = useRef();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const { data: userProfile } = useGetUserProfileQuery();

  const locale = userLocale(userProfile);

  const [ingredientSuggestions, setIngredientSuggestions] = useState([]);

  const [
    ingredientSuggestionsNetworkState,
    setIngredientSuggestionsNetworkState,
  ] = useState({ loading: false, loaded: false, error: null });

  const loadSuggestionsFromHistory = async (ingredient) => {
    const ingredientEditSuggestions = await getSuggestionsFromHistoryOperation(
      ingredient.id,
      // ingredient.fullText,
    );
    const historySuggestions = ingredientEditSuggestions.map(
      (ingredientEditSuggestion) => ({
        suggestionText: ingredientEditSuggestion.suggestion,
        suggestionType: 'history',
        highlightTokens: [],
      }),
    );
    setIngredientSuggestions((prevIngredientSuggestions) => [
      ...prevIngredientSuggestions,
      ...historySuggestions,
    ]);
  };

  const loadSuggestionsFromFoodBrain = async (ingredient) => {
    const suggestionsResponse = await getSuggestionsOperation(
      [ingredient],
      locale,
      customerFoodDataID,
    );
    setIngredientSuggestions((prevIngredientSuggestions) => [
      ...prevIngredientSuggestions,
      ...suggestionsResponse[0].suggestions,
    ]);
  };

  const resetSuggestions = () => {
    setIngredientSuggestions([]);
    setIngredientSuggestionsNetworkState({
      loading: false,
      loaded: false,
      error: null,
    });
  };

  const loadSuggestions = async (ingredient) => {
    setIngredientSuggestions([]);
    setIngredientSuggestionsNetworkState({ loading: true });
    try {
      await Promise.all([
        loadSuggestionsFromHistory(ingredient),
        loadSuggestionsFromFoodBrain(ingredient),
      ]);
      setIngredientSuggestionsNetworkState({
        loading: false,
        loaded: true,
        error: null,
      });
    } catch (e) {
      setIngredientSuggestionsNetworkState({
        loading: false,
        loaded: false,
        error: e.toString(),
      });
    }
  };

  const onInlineEditFocus = (section, itemId) => {
    console.log(`Focus ${section} ${itemId}`);
    const value =
      itemId !== null
        ? currentRecipeRef.current[section].find((it) => it.id === itemId)
            .fullText
        : currentRecipeRef.current[section] || '';
    currentTextRef.current = ['mealTypes', 'utensils'].includes(section)
      ? value
      : value.toString();
    if (section === 'ingredients') {
      const ingredient = currentRecipeRef.current[section].find(
        (it) => it.id === itemId,
      );
      loadSuggestions(ingredient);
    }
    setCurrentlyEditingInline({ section, itemId });
  };

  const onInlineEditChange = (section, itemId, value) => {
    console.log(`Change ${section} ${itemId}`);
    currentTextRef.current = value;
  };

  const dismissEditingInline = () => {
    currentTextRef.current = null;
    setCurrentlyEditingInline({ section: null, itemId: null });
  };

  const focusOnField = (section, id) => {
    setCurrentlyEditingInline({ section, itemId: id });
    window.setTimeout(() => {
      const el = document.getElementById(`${section}-${id}`);
      if (el) {
        el.focus();
      }
    }, 100);
  };

  const currentlyEditingMeal = { recipes: [currentRecipeRef.current] };

  const onIngredientsUpdate = useCallback(async () => {
    const { ingsFullText, analyzedIngredients } =
      await analyzeMissingIngredientsOperation(
        currentRecipeRef.current.ingredients,
        customerFoodDataID,
      );
    for (let i = 0; i < ingsFullText.length; i += 1) {
      if (analyzedIngredients[i]) {
        analyzedIngredients[i].fullText = ingsFullText[i];
      }
    }
    const updatedMeal = mealWithIngredientTokensUpdated(
      { recipes: [currentRecipeRef.current] },
      analyzedIngredients,
    );
    const deriveNutritionResult = await deriveNutritionFromIngredientsOperation(
      updatedMeal.recipes[0].ingredients,
      updatedMeal.recipes[0].servings,
      updatedMeal.recipes[0].publisher,
      updatedMeal.recipes[0].chef,
      customerFoodDataID,
      updatedMeal.derivedNutrition?.ingredientNutrition,
    );
    setDerivedNutrition(deriveNutritionResult);
    [currentRecipeRef.current] = updatedMeal.recipes;
  }, [customerFoodDataID]);

  const onInlineEditBlur = async (section, itemId, reason) => {
    console.log(`Blur ${section} ${itemId} ${reason}`);
    // let shouldDismissEditingInline = true;
    if (
      [
        'title',
        'shortDescription',
        'chef',
        'servings',
        'prepTime',
        'cookTime',
        'mealTypes',
        'utensils',
        'recipeNotes',
      ].includes(section)
    ) {
      currentRecipeRef.current[section] = currentTextRef.current;
    } else if (section === 'ingredients') {
      if (reason !== 'smorgCancel') {
        const { ingredients } = currentRecipeRef.current;
        const index = ingredients.findIndex((ing) => ing.id === itemId);
        console.log(
          `Reason is ${reason}, editing item at index ${index} out of ${ingredients.length}`,
        );
        // const editMethod =
        //   reason === 'selectOption' ? 'OPTION_SELECTED' : 'TEXT_TYPED';
        const appendNewItem =
          reason === 'enter-key' && index === ingredients.length - 1;
        const appendedNewItemId = appendNewItem && uuid();
        console.log({ appendedNewItemId });
        let updatedMeal = mealWithIngredientTokensReset(
          { recipes: [currentRecipeRef.current] },
          itemId,
        );
        updatedMeal = mealWithArraySectionUpdated(
          updatedMeal,
          section,
          itemId,
          currentTextRef.current,
        );
        if (appendedNewItemId) {
          const appendIndex = currentRecipeRef.current[section].length;
          updatedMeal = mealWithNewArraySectionItem(
            updatedMeal,
            section,
            appendIndex,
            appendedNewItemId,
            '',
          );
        }
        [currentRecipeRef.current] = updatedMeal.recipes;
        await onIngredientsUpdate();
        if (appendNewItem) {
          currentTextRef.current = '';
          focusOnField(section, appendedNewItemId);
        }
      }
    }

    dismissEditingInline();
  };

  const onAddArraySectionItem = (section) => {
    const addedIndex = (currentRecipeRef.current[section] || []).length;
    const newId = uuid();
    const updatedMeal = mealWithNewArraySectionItem(
      currentlyEditingMeal,
      section,
      addedIndex,
      newId,
      '',
    );
    [currentRecipeRef.current] = updatedMeal.recipes;
    resetSuggestions();
    focusOnField(section, newId);
  };

  const onRemoveArraySectionItem = async (section, itemId) => {
    const updatedMeal = mealWithArraySectionDeleted(
      currentlyEditingMeal,
      section,
      itemId,
    );
    [currentRecipeRef.current] = updatedMeal.recipes;
    await onIngredientsUpdate();
  };

  const onUpdateIngredientStructuredData = async (
    updatedIngredient,
    updatedIngredientNutrition,
  ) => {
    const updatedMeal = mealWithStructuredIngredientUpdated(
      currentlyEditingMeal,
      updatedIngredient,
      updatedIngredientNutrition,
    );
    [currentRecipeRef.current] = updatedMeal.recipes;
    await onIngredientsUpdate();
  };

  const handleClickLog = () => {
    onClickLog({ recipes: [currentRecipeRef.current] }, derivedNutrition);
  };

  useEffect(() => {
    [currentRecipeRef.current] = meal.recipes;
    setScaleToServings(meal.recipes[0]?.servings);
    if (!derivedNutrition) {
      onIngredientsUpdate();
    }
  }, [derivedNutrition, meal.recipes, onIngredientsUpdate]);

  return (
    <Dialog
      open
      maxWidth="md"
      // Prevents TinyMCE issue - disabled inputs in Image dialogs invoked from toolbar
      disableEnforceFocus
      fullWidth
      onClose={onCloseMealDetail}>
      <div style={{ padding: '20px 20px 5px' }}>
        <p style={{ paddingBottom: '8px' }}>
          Check and Edit your food item, log when ready
        </p>
        <div>
          <Button variant="contained" onClick={handleClickLog}>
            Log
          </Button>
          <Button
            variant="contained"
            onClick={onCloseMealDetail}
            style={{ marginLeft: '6px' }}>
            Cancel
          </Button>
        </div>
      </div>
      {currentlyEditingMeal.recipes[0] && (
        <MealDetailEditableMobile
          menu={null}
          meal={currentlyEditingMeal}
          savingInProgress={false}
          showNutritionMetrics={showNutritionMetrics}
          alwaysShowNutrition
          showMacroRatio
          derivedNutrition={derivedNutrition}
          canScaleIngredients={false}
          scaleToServings={scaleToServings}
          currentlyEditingInline={currentlyEditingInline}
          ingredientSuggestions={ingredientSuggestions}
          ingredientSuggestionsNetworkState={ingredientSuggestionsNetworkState}
          onInlineEditFocus={onInlineEditFocus}
          onInlineEditChange={onInlineEditChange}
          onInlineEditBlur={onInlineEditBlur}
          onAddArraySectionItem={onAddArraySectionItem}
          onRemoveArraySectionItem={onRemoveArraySectionItem}
          onIngredientCheckChange={() => {}}
          onEditScaleToServings={() => {}}
          onChangeScaleToServings={() => {}}
          resetScaleToServings={() => {}}
          onImageGeneratedAI={() => {}}
          onNewImageChosen={() => {}}
          onAddonChange={() => {}}
          onTagsChange={() => {}}
          onDialogClose={onCloseMealDetail}
          onUpdateIngredientStructuredData={onUpdateIngredientStructuredData}
          initialIngredientsEditMode
          hideFieldsForLoggingFoodItem
        />
      )}
    </Dialog>
  );
};

LogFoodItemMealDetailPage.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  meal: PropTypes.object.isRequired,
  onClickLog: PropTypes.func.isRequired,
  onCloseMealDetail: PropTypes.func.isRequired,
};

export default LogFoodItemMealDetailPage;
